import { useEffect, useRef, useState, useCallback } from 'react';
import type { Map, Marker, Circle, LatLng } from 'leaflet';

interface MapPickerProps {
  latitude?: number;
  longitude?: number;
  geofenceRadius?: number;
  onLocationSelect: (lat: number, lng: number) => void;
  disabled?: boolean;
  height?: string;
}

interface GeocodeResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
}

export function MapPicker({
  latitude,
  longitude,
  geofenceRadius = 50,
  onLocationSelect,
  disabled = false,
  height = '350px',
}: MapPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const circleRef = useRef<Circle | null>(null);
  const disabledRef = useRef(disabled);
  const radiusRef = useRef(geofenceRadius);
  const prevCoordsRef = useRef<{ lat?: number; lng?: number }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => { disabledRef.current = disabled; }, [disabled]);
  useEffect(() => {
    radiusRef.current = geofenceRadius;
    if (circleRef.current) {
      circleRef.current.setRadius(geofenceRadius);
    }
  }, [geofenceRadius]);

  const onLocationSelectMemo = useCallback((lat: number, lng: number) => {
    onLocationSelect(lat, lng);
  }, [onLocationSelect]);

  const updateMarkerAndCircle = useCallback(async (map: Map, lat: number, lng: number) => {
    const L = await import('leaflet');

    if (markerRef.current) map.removeLayer(markerRef.current);
    if (circleRef.current) map.removeLayer(circleRef.current);

    const marker = L.marker([lat, lng], { draggable: !disabledRef.current }).addTo(map);
    const circle = L.circle([lat, lng], {
      radius: radiusRef.current,
      color: '#3b82f6',
      fillColor: '#3b82f6',
      fillOpacity: 0.12,
      weight: 2,
    }).addTo(map);

    markerRef.current = marker;
    circleRef.current = circle;
    prevCoordsRef.current = { lat, lng };

    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      prevCoordsRef.current = { lat: pos.lat, lng: pos.lng };
      circle.setLatLng(pos);
      onLocationSelectMemo(pos.lat, pos.lng);
    });
  }, [onLocationSelectMemo]);

  // Init map
  useEffect(() => {
    if (!mapRef.current || disabled) {
      setIsLoading(false);
      return;
    }
    if (mapInstanceRef.current) {
      setIsLoading(false);
      return;
    }

    const loadMap = async () => {
      try {
        const L = await import('leaflet');
        await import('leaflet/dist/leaflet.css');

        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        const defaultLat = latitude || 44.7866;
        const defaultLng = longitude || 20.4489;

        if (!mapRef.current) return;

        const map = L.map(mapRef.current, {
          center: [defaultLat, defaultLng],
          zoom: latitude && longitude ? 16 : 10,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;

        // Click to place marker
        map.on('click', (e: { latlng: LatLng }) => {
          if (disabledRef.current) return;
          updateMarkerAndCircle(map, e.latlng.lat, e.latlng.lng);
          onLocationSelectMemo(e.latlng.lat, e.latlng.lng);
        });

        // Initial marker
        if (latitude && longitude && !(latitude === 0 && longitude === 0)) {
          updateMarkerAndCircle(map, latitude, longitude);
        }

        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };

    loadMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, [disabled, latitude, longitude, onLocationSelectMemo, updateMarkerAndCircle]);

  // Update marker from external coord changes
  useEffect(() => {
    if (!mapInstanceRef.current || !latitude || !longitude) return;
    if (prevCoordsRef.current.lat === latitude && prevCoordsRef.current.lng === longitude) return;

    updateMarkerAndCircle(mapInstanceRef.current, latitude, longitude);
    mapInstanceRef.current.setView([latitude, longitude], 16);
  }, [latitude, longitude, updateMarkerAndCircle]);

  // Address search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!searchQuery.trim()) { setSearchResults([]); setShowResults(false); return; }
      setIsSearching(true);
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`)
        .then(r => r.json())
        .then((data: GeocodeResult[]) => { setSearchResults(data); setShowResults(data.length > 0); })
        .catch(() => { setSearchResults([]); setShowResults(false); })
        .finally(() => setIsSearching(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectResult = async (result: GeocodeResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], 16);
      await updateMarkerAndCircle(mapInstanceRef.current, lat, lng);
      onLocationSelectMemo(lat, lng);
    }
    setSearchQuery(result.display_name);
    setShowResults(false);
  };

  // Close results on outside click
  useEffect(() => {
    if (!showResults) return;
    const handle = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.map-search-container')) setShowResults(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showResults]);

  return (
    <div className="relative w-full">
      {/* Search */}
      <div className="relative mb-2 map-search-container">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          placeholder="Pretraži adresu..."
          disabled={disabled || isLoading}
          className="w-full rounded-lg border border-input bg-background h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          </div>
        )}
        {showResults && searchResults.length > 0 && (
          <div className="absolute w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-[99999]">
            {searchResults.map((r) => (
              <button
                key={r.place_id}
                type="button"
                onClick={() => handleSelectResult(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border last:border-0 cursor-pointer"
              >
                {r.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="relative w-full rounded-lg border border-input overflow-hidden" style={{ height }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        )}
        <div ref={mapRef} className="w-full h-full" style={{ height }} />
        <div className="absolute bottom-2 left-2 bg-background/90 px-2 py-1 rounded text-xs text-muted-foreground z-30">
          Klikni na mapu da postaviš lokaciju
        </div>
      </div>
    </div>
  );
}
